import { type JSX, useRef, useState } from 'react';

import type { Palette } from '@schema/index.js';

import { type BuilderStore, STARTER_THEME } from '../state.js';
import { THEME_PRESETS } from './theme-presets.js';

import { ColorField, SelectField, SubTabs, type SubTabSpec, TextField } from './Field.js';

export interface ThemeEditorProps {
  store: BuilderStore;
}

const DARK_MODES = [
  { value: 'auto', label: 'Auto (follow OS)' },
  { value: 'always', label: 'Always dark' },
  { value: 'never', label: 'Always light' },
] as const;

/**
 * Renderer's :root defaults for the rich palette — surfaced as
 * placeholders so the customer sees what they're overriding. Keep in
 * sync with packages/renderer-react/src/styles/index.css (light) and
 * the .profile-view[data-theme='always'] block (dark). Also mirrored
 * in the STARTER_CONFIG / signup default so all four surfaces (renderer
 * fallback, Builder placeholder, Builder starter, fresh-signup config)
 * agree on what "default MobiLauncher chrome" looks like.
 */
const DEFAULT_LIGHT_PALETTE: Record<keyof Palette, string> = {
  background: '#F5F7FA',
  surface:    '#FFFFFF',
  primary:    '#1976D2',
  accent:     '#03DAC6',
  text:       '#0F172A',
  textMuted:  '#64748B',
  headerBg:   '#0F2A4A',
  headerInk:  '#FFFFFF',
  footerBg:   '#FFFFFF',
  heroBg:     '#0F2A4A',
  tileBg:     '#FFFFFF',
  tileBorder: '#E2E8F0',
  cardBg:     '#FFFFFF',
  cta:        '#1976D2',
  iconAccent: '#1976D2',
  divider:    '#E2E8F0',
};
const DEFAULT_DARK_PALETTE: Record<keyof Palette, string> = {
  background: '#0B1220',
  surface:    '#131C2E',
  primary:    '#5BAEEF',
  accent:     '#03DAC6',
  text:       '#E2E8F0',
  textMuted:  '#94A3B8',
  headerBg:   '#0F2A4A',
  headerInk:  '#FFFFFF',
  footerBg:   '#131C2E',
  heroBg:     '#0F2A4A',
  tileBg:     '#1A2540',
  tileBorder: '#2A3552',
  cardBg:     '#131C2E',
  cta:        '#1976D2',
  iconAccent: '#03DAC6',
  divider:    '#1F2A44',
};

/** Per-section grouping of the Palette fields for the picker UI. */
interface PaletteGroup {
  label: string;
  fields: ReadonlyArray<{
    field: keyof Palette;
    label: string;
    hint: string;
  }>;
}
const PALETTE_GROUPS: readonly PaletteGroup[] = [
  {
    label: 'Surfaces',
    fields: [
      { field: 'background', label: 'Background', hint: 'Page background behind the tile grid.' },
      { field: 'surface',    label: 'Surface',    hint: 'Default surface — propagates to cards, footer, tiles when those aren\'t overridden.' },
    ],
  },
  {
    label: 'Chrome',
    fields: [
      { field: 'headerBg',  label: 'Header bg',  hint: 'Top status bar background.' },
      { field: 'headerInk', label: 'Header ink', hint: 'Text + icon color on the top bar.' },
      { field: 'heroBg',    label: 'Hero bg',    hint: 'Slab behind the featured-tile band. Falls back to header bg.' },
      { field: 'footerBg',  label: 'Footer bg',  hint: 'Bottom strip background. Falls back to surface.' },
    ],
  },
  {
    label: 'Tiles & cards',
    fields: [
      { field: 'tileBg',     label: 'Tile bg',     hint: 'App / web / agent-action tile background.' },
      { field: 'tileBorder', label: 'Tile border', hint: 'Tile outline color — also the "accent around icon cards".' },
      { field: 'cardBg',     label: 'Card bg',     hint: 'Device Info, Tools, broadcast cards.' },
    ],
  },
  {
    label: 'Text',
    fields: [
      { field: 'text',      label: 'Text',       hint: 'Primary text color.' },
      { field: 'textMuted', label: 'Text muted', hint: 'Secondary / supporting text color.' },
    ],
  },
  {
    label: 'Accents',
    fields: [
      { field: 'primary',    label: 'Primary',     hint: 'Brand color — launch buttons, headers.' },
      { field: 'accent',     label: 'Accent',      hint: 'Secondary highlight; tile focus rings, links.' },
      { field: 'cta',        label: 'CTA',         hint: 'Primary buttons + admin actions.' },
      { field: 'iconAccent', label: 'Icon accent', hint: 'Stroke color for monochrome SVG icons inside dialogs.' },
      { field: 'divider',    label: 'Divider',     hint: 'Section / row separator lines.' },
    ],
  },
];

type ThemeTabId =
  | 'brand'
  | 'paletteLight'
  | 'paletteDark'
  | 'wallpaper'
  | 'logo';
const THEME_TABS: readonly SubTabSpec<ThemeTabId>[] = [
  { id: 'brand',        label: 'Brand' },
  { id: 'paletteLight', label: 'Light palette', badge: 'Pro' },
  { id: 'paletteDark',  label: 'Dark palette',  badge: 'Pro' },
  { id: 'wallpaper',    label: 'Wallpaper',     badge: 'Pro' },
  { id: 'logo',         label: 'Logo & font',   badge: 'Pro' },
];

/**
 * Theme editor.
 *
 * Every tier sees the polished light + dark default palettes ship
 * (STARTER_THEME — basic 6 fields + paletteLight + paletteDark). What
 * differs by tier is what's *editable*:
 *
 *   Brand        (every tier)  — light/dark mode, Primary + Accent.
 *                                These are the only two color knobs Free
 *                                tenants get. Edits flow through the
 *                                renderer's brand-carry cascade so they
 *                                paint in both modes without dual-write.
 *   Light palette (Pro)         — full Palette editor for light mode:
 *                                surfaces, chrome, tiles+cards, text,
 *                                accents.
 *   Dark palette  (Pro)         — same shape, mirrored for dark.
 *   Wallpaper     (Pro)         — hero-band background image upload.
 *   Logo & font   (Pro)         — header logo + custom font asset ref.
 *
 * Free can preview the Pro tabs (they show locked teasers); the runtime
 * gates whether the Pro fields actually apply, but the renderer already
 * paints paletteLight/paletteDark at every tier so the polished default
 * looks coherent without an upgrade.
 */
export function ThemeEditor({ store }: ThemeEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const { theme } = profile;
  const proHonored = store.config.enabledFeatures.includes('theming');
  const [tab, setTab] = useState<ThemeTabId>('brand');

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Theme</h2>
        <p className="editor__sub">
          Pick light / dark mode and your brand colors. Every tier ships
          a polished default light + dark palette out of the box.{' '}
          <strong>Pro</strong> unlocks the per-surface palette editors
          (header, hero, tile borders, cards), a custom logo + font, and
          a hero wallpaper.{' '}
          <a href="https://mobilauncher.com/docs/customize#theme" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <SubTabs<ThemeTabId>
        tabs={THEME_TABS}
        active={tab}
        onChange={setTab}
        label="Theme sections"
      />

      {tab === 'brand' && (
        <>
          <fieldset className="editor__group">
            <legend className="editor__group-label">Light / dark mode</legend>
            <SelectField
              label="Default mode"
              hint="`Auto` follows the device's OS color-scheme. The launcher's header menu (Pro) lets each device override locally."
              value={theme.darkMode ?? 'auto'}
              options={DARK_MODES}
              onChange={(value) => store.patchTheme({ darkMode: value })}
            />
          </fieldset>

          {/* Brand colors — the two knobs that are meaningful at every
              tier. Edit theme.primary / theme.accent (flat fields).
              Renderer's brand carry uses these in BOTH modes when
              paletteDark/paletteLight don't override, so a single
              edit propagates to light + dark without dual-write
              gymnastics. */}
          <fieldset className="editor__group" data-testid="theme-brand-fieldset">
            <legend className="editor__group-label">Brand colors</legend>
            <ColorField
              label="Primary"
              hint="Your brand color — launch buttons, focused tiles. Applied in both light and dark mode."
              value={theme.primary}
              onChange={(value) => store.patchTheme({ primary: value })}
            />
            <ColorField
              label="Accent"
              hint="Highlight color — tile focus rings, links, the icon on the launcher header brand mark."
              value={theme.accent}
              onChange={(value) => store.patchTheme({ accent: value })}
            />
          </fieldset>

          <fieldset className="editor__group">
            <legend className="editor__group-label">Presets</legend>
            <p className="field__hint">
              One-click palettes for customers who'd rather pick than
              author. Each preset writes a coherent light + dark pair —
              your tiles, logo, and wallpaper are preserved.
            </p>
            <div className="theme-presets" data-testid="theme-presets">
              {THEME_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="theme-presets__swatch"
                  onClick={() => store.patchProfile({ theme: p.theme })}
                  title={`Apply ${p.name}`}
                  data-testid={`theme-preset-${p.id}`}
                >
                  <span
                    className="theme-presets__swatch-bg"
                    style={{ background: p.swatchBg }}
                  >
                    <span
                      className="theme-presets__swatch-accent"
                      style={{ background: p.swatchAccent }}
                    />
                  </span>
                  <span className="theme-presets__swatch-name">{p.name}</span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className="builder__btn builder__btn--ghost"
              onClick={() => {
                store.patchProfile({ theme: STARTER_THEME });
              }}
            >
              ↺ Reset to MobiLauncher defaults
            </button>
            <p className="field__hint">
              Reset overwrites every color, palette, logo, and wallpaper
              with the brand defaults — useful when the launcher's
              looking off and you want a clean slate.
            </p>
          </fieldset>

          {!proHonored && (
            <p className="field__hint">
              Want chrome-level overrides (header bar color, footer band,
              tile borders, cards)? Upgrade to <strong>Pro</strong> to
              edit the Light + Dark palettes on this page.
            </p>
          )}
        </>
      )}

      {tab === 'paletteLight' && (
        <AdvancedPalette
          mode="light"
          proHonored={proHonored}
          value={theme.paletteLight ?? {}}
          onChange={(next) =>
            store.patchTheme({
              paletteLight: Object.keys(next).length === 0 ? undefined : next,
            })
          }
        />
      )}

      {tab === 'paletteDark' && (
        <AdvancedPalette
          mode="dark"
          proHonored={proHonored}
          value={theme.paletteDark ?? {}}
          onChange={(next) =>
            store.patchTheme({
              paletteDark: Object.keys(next).length === 0 ? undefined : next,
            })
          }
        />
      )}

      {tab === 'wallpaper' && (
        <fieldset className="editor__group">
          <legend className="editor__group-label">
            Hero wallpaper {proHonored ? '' : '(Pro)'}
          </legend>
          <p className="field__hint">
            Image sits behind the featured-tile band at the top of the
            launcher. The tile grid below uses the <code>Background</code>{' '}
            color from the Colors tab so app labels stay legible.
          </p>
          <WallpaperField
            value={theme.wallpaperDataUrl}
            onChange={(value) =>
              store.patchTheme({
                ...(value === undefined ? { wallpaperDataUrl: undefined } : { wallpaperDataUrl: value }),
              })
            }
          />
        </fieldset>
      )}

      {tab === 'logo' && (
        <fieldset className="editor__group">
          <legend className="editor__group-label">
            Logo & font {proHonored ? '' : '(Pro)'}
          </legend>
          <LogoField
            value={theme.logoDataUrl}
            onChange={(value) =>
              store.patchTheme({
                ...(value === undefined ? { logoDataUrl: undefined } : { logoDataUrl: value }),
              })
            }
          />
          <TextField
            label="Custom font asset ref"
            hint="Path the device can read at runtime — typically a /sdcard/launcher/fonts/ asset your MDM ships alongside the launcher bundle."
            mono
            placeholder="/sdcard/launcher/fonts/myFont.ttf"
            value={theme.fontRef ?? ''}
            onChange={(value) => store.patchTheme({ fontRef: value })}
          />
        </fieldset>
      )}
    </div>
  );
}

/**
 * Per-mode advanced-palette editor. Used as the body of the Advanced
 * sub-tabs (one for light, one for dark). PALETTE_GROUPS-worth of
 * ColorFields, grouped by surface category. Mutates an immutable
 * Palette object via onChange — a field set to '' is removed from the
 * object so the renderer falls back to its baked-in default.
 */
function AdvancedPalette({
  mode,
  proHonored,
  value,
  onChange,
}: {
  mode: 'light' | 'dark';
  proHonored: boolean;
  value: Palette;
  onChange: (next: Palette) => void;
}): JSX.Element {
  const defaults = mode === 'light' ? DEFAULT_LIGHT_PALETTE : DEFAULT_DARK_PALETTE;
  const intro =
    mode === 'light'
      ? "Per-surface colors applied in light mode: header bar, footer band, hero, tile borders, cards, CTAs. Each field is optional — blank inherits from the Colors tab or the launcher's tuned default."
      : "Applied when the device renders in dark mode (Auto + OS preference, or Always dark in Colors). Blank fields inherit the launcher's tuned dark defaults.";

  function patchField(field: keyof Palette, next: string): void {
    const merged: Palette = { ...value };
    if (next.length === 0) {
      delete merged[field];
    } else {
      merged[field] = next;
    }
    onChange(merged);
  }

  return (
    <>
      <p className="field__hint">
        {intro}
        {!proHonored && (
          <>
            {' '}
            <strong>Pro feature</strong> — edits preview here but only apply on a
            Pro/Enterprise license.
          </>
        )}
      </p>
      {PALETTE_GROUPS.map((g) => (
        <fieldset key={g.label} className="editor__group">
          <legend className="editor__group-label">{g.label}</legend>
          {g.fields.map((f) => (
            <ColorField
              key={f.field}
              label={f.label}
              hint={f.hint}
              value={value[f.field] ?? ''}
              defaultValue={defaults[f.field]}
              onChange={(v) => patchField(f.field, v)}
            />
          ))}
        </fieldset>
      ))}
    </>
  );
}

/**
 * Drag-and-drop hero-background uploader. Stores the picked image
 * inline in the config as a base64 data URL — no separate asset
 * hosting needed for self-service tenants. The image renders behind
 * the featured-tile band only, not the whole launcher (the tile grid
 * keeps the solid `background` color so labels stay legible).
 * Pictures larger than 1920×1080 are scaled down on a canvas before
 * encoding so the config blob stays under the schema's 3 MB cap.
 * JPEG q=0.85 hits a good size / quality balance for typical photos.
 */
function WallpaperField({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function ingest(file: File): Promise<void> {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('File must be an image (JPEG, PNG, WebP, etc.).');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      if (dataUrl.length > 3_000_000) {
        setError(
          `Image is ${(dataUrl.length / 1024 / 1024).toFixed(1)} MB after encoding — try a smaller source or a JPEG.`,
        );
        return;
      }
      onChange(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field wallpaper-field">
      <span className="field__label">Wallpaper</span>
      <div
        className={`wallpaper-field__drop${isDragOver ? ' wallpaper-field__drop--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          const f = e.dataTransfer.files[0];
          if (f) void ingest(f);
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="wallpaper-drop"
      >
        {value ? (
          <img className="wallpaper-field__preview" src={value} alt="Wallpaper preview" />
        ) : (
          <span className="wallpaper-field__placeholder">
            {busy ? 'Reading…' : 'Drop an image here, or click to pick'}
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void ingest(f);
            e.target.value = '';
          }}
          data-testid="wallpaper-file-input"
        />
      </div>
      <div className="wallpaper-field__row">
        <span className="field__hint">
          Recommended <strong>1920×1080</strong> (16:9 landscape) JPEG or PNG.
          Larger images are automatically downscaled. Max ~3&nbsp;MB after
          encoding.
        </span>
        {value && (
          <button
            type="button"
            className="wallpaper-field__clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange(undefined);
              setError(null);
            }}
            data-testid="wallpaper-clear"
          >
            Clear
          </button>
        )}
      </div>
      {error !== null && (
        <span className="field__hint field__hint--err" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

const MAX_WALLPAPER_DIM = 1920;

async function fileToDataUrl(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageEl(url);
    const scale = Math.min(
      MAX_WALLPAPER_DIM / img.naturalWidth,
      MAX_WALLPAPER_DIM / img.naturalHeight,
      1,
    );
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('canvas 2d context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to load'));
    img.src = src;
  });
}

/**
 * Drag-and-drop logo uploader. Same drop-zone pattern as the
 * wallpaper uploader, sized for square brand artwork. The image is
 * downscaled to a 256×256 bounding box and re-encoded as PNG (PNG
 * preserves transparent backgrounds — important for logos overlaying
 * the launcher header). 500 KB encoded cap matches the schema.
 */
function LogoField({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function ingest(file: File): Promise<void> {
    setError(null);
    if (!file.type.startsWith('image/')) {
      setError('File must be an image (PNG, SVG, JPEG, WebP).');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToLogoDataUrl(file);
      if (dataUrl.length > 500_000) {
        setError(
          `Logo is ${(dataUrl.length / 1024).toFixed(0)} KB after encoding — try a smaller source.`,
        );
        return;
      }
      onChange(dataUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read the image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="field wallpaper-field wallpaper-field--logo">
      <span className="field__label">Logo</span>
      <div
        className={`wallpaper-field__drop wallpaper-field__drop--logo${isDragOver ? ' wallpaper-field__drop--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          const f = e.dataTransfer.files[0];
          if (f) void ingest(f);
        }}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        data-testid="logo-drop"
      >
        {value ? (
          <img className="wallpaper-field__preview wallpaper-field__preview--logo" src={value} alt="Logo preview" />
        ) : (
          <span className="wallpaper-field__placeholder">
            {busy ? 'Reading…' : 'Drop a logo here, or click to pick'}
          </span>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void ingest(f);
            e.target.value = '';
          }}
          data-testid="logo-file-input"
        />
      </div>
      <div className="wallpaper-field__row">
        <span className="field__hint">
          Recommended <strong>256×256 PNG</strong> with a transparent background.
          Square or near-square reads best on the launcher's top bar. Sources
          larger than 256 px are downscaled before encoding. Max ~500&nbsp;KB.
        </span>
        {value && (
          <button
            type="button"
            className="wallpaper-field__clear"
            onClick={(e) => {
              e.stopPropagation();
              onChange(undefined);
              setError(null);
            }}
            data-testid="logo-clear"
          >
            Clear
          </button>
        )}
      </div>
      {error !== null && (
        <span className="field__hint field__hint--err" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

const MAX_LOGO_DIM = 256;

async function fileToLogoDataUrl(file: File): Promise<string> {
  // SVG can be base64'd as-is — no decode-encode round trip needed and
  // the renderer scales it natively without quality loss.
  if (file.type === 'image/svg+xml') {
    const text = await file.text();
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`;
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImageEl(url);
    const scale = Math.min(
      MAX_LOGO_DIM / img.naturalWidth,
      MAX_LOGO_DIM / img.naturalHeight,
      1,
    );
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx === null) throw new Error('canvas 2d context unavailable');
    ctx.drawImage(img, 0, 0, w, h);
    // PNG preserves transparency; JPEG would replace transparent pixels
    // with black which looks awful when the logo is overlaid on a
    // brand-colored bar.
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}
